import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  MenuItem, IconButton, Tooltip, Divider, Chip
} from '@mui/material';
import { Home, Receipt, Print, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const BillingForm = () => {
  const { state } = useLocation();
  const navigate  = useNavigate();

  const [chargeItems, setChargeItems] = useState([]);
  const [glAccounts,  setGlAccounts]  = useState([]);
  const [form, setForm] = useState({
    learnerAdmNo: state?.learnerAdmNo || '',
    stage:        state?.stage        || '',
    chargeItemCode: '',
    description:  '',
    reason:       '',
    glAccount:    '',
    amount:       '',
  });
  const [error,          setError]          = useState('');
  const [createdInvoice, setCreatedInvoice] = useState(null); // holds invoice after success
  const [saving,         setSaving]         = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [ciRes, glRes] = await Promise.all([
          axios.get(`${API}/api/academics/charge-items`, { headers: hdrs() }),
          axios.get(`${API}/api/finance/gl-accounts`,    { headers: hdrs() }),
        ]);
        setChargeItems((ciRes.data || []).filter(c => c.isActive));
        setGlAccounts(glRes.data || []);
      } catch { /* silent */ }
    };
    load();
  }, []);

  const handleChargeItem = (code) => {
    const item = chargeItems.find(c => c.code === code);
    setForm(f => ({
      ...f,
      chargeItemCode: code,
      description:    item?.name     || f.description,
      glAccount:      item?.glAccount || '',
    }));
  };

  const glLabel = (accountNo) => {
    const acc = glAccounts.find(a => a.accountNo === accountNo);
    return acc ? `${acc.accountNo} — ${acc.accountName}` : accountNo;
  };

  const handleSubmit = async () => {
    if (!form.learnerAdmNo)    { setError('Learner Adm No is required'); return; }
    if (!form.chargeItemCode)  { setError('Select a charge item'); return; }
    if (!form.amount || isNaN(Number(form.amount))) { setError('Enter a valid amount'); return; }
    if (!form.glAccount)       { setError('The selected charge item has no income account — set its GL account under Academics → Charge Items.'); return; }
    setSaving(true); setError('');
    try {
      const res = await axios.post(
        `${API}/api/finance/invoices`,
        {
          learnerAdmNo: form.learnerAdmNo,
          stage:        form.stage,
          description:  form.description,
          reason:       form.reason,
          glAccount:    form.glAccount,
          amount:       Number(form.amount),
        },
        { headers: hdrs() }
      );
      setCreatedInvoice({
        ...res.data.invoice,
        chargeItemCode: form.chargeItemCode,
        glAccountLabel: glLabel(form.glAccount),
      });
      setForm(f => ({ ...f, chargeItemCode: '', description: '', reason: '', glAccount: '', amount: '' }));
    } catch (err) {
      setError(err.response?.data?.error || 'Billing failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const selectedItem = chargeItems.find(c => c.code === form.chargeItemCode);
  const printDate    = createdInvoice ? new Date(createdInvoice.date || createdInvoice.createdAt).toLocaleDateString(locale(), { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #billing-print-area, #billing-print-area * { visibility: visible; }
          #billing-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 40px; box-sizing: border-box; font-family: serif; }
        }
        @media screen {
          #billing-print-area { display: none; }
        }
      `}</style>

      {/* Hidden print area */}
      {createdInvoice && (
        <Box id="billing-print-area">
          <Box textAlign="center" mb={3}>
            <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: 1 }}>INVOICE</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>Invoice No: <strong>{createdInvoice.invoiceNo}</strong></Typography>
            <Typography variant="body2">Date: {printDate}</Typography>
          </Box>

          <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="body2"><strong>Billed To:</strong> {createdInvoice.learnerAdmNo}</Typography>
            <Typography variant="body2"><strong>Stage:</strong> {createdInvoice.stage || '—'}</Typography>
          </Box>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Reason / Ref</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>Amount ({sym()})</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{createdInvoice.description}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{createdInvoice.reason || '—'}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>
                  {fmt(createdInvoice.amount)}
                </td>
              </tr>
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>TOTAL</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>
                  {sym()} {fmt(createdInvoice.amount)}
                </td>
              </tr>
            </tbody>
          </table>

          <Typography variant="caption" color="text.secondary">GL Account: {createdInvoice.glAccountLabel}</Typography>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Prepared by: _______________________</Typography>
            <Typography variant="caption">Authorized by: _______________________</Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption">Status: <strong>{createdInvoice.status}</strong></Typography>
          </Box>
        </Box>
      )}

      {/* Screen UI */}
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, mb: 4 }}>
        <Card elevation={3}>
          <CardContent>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Receipt color="primary" />
                <Typography variant="h5" color="primary">Bill Learner</Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button startIcon={<ArrowBack />} size="small" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
                <Tooltip title="Dashboard">
                  <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper' }}>
                    <Home />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Success state */}
            {createdInvoice && (
              <Alert severity="success" sx={{ mb: 2 }}
                action={
                  <Box display="flex" gap={1}>
                    <Button size="small" startIcon={<Print />} variant="contained" onClick={handlePrint}>
                      Print Invoice
                    </Button>
                    <Button size="small" onClick={() => navigate('/learners')}>Learners</Button>
                  </Box>
                }>
                Invoice <strong>{createdInvoice.invoiceNo}</strong> created successfully.
              </Alert>
            )}

            <Box display="flex" flexDirection="column" gap={2}>
              {/* Learner */}
              <Box display="flex" gap={2}>
                <TextField label="Learner Adm No" value={form.learnerAdmNo} size="small"
                  onChange={e => setForm(f => ({ ...f, learnerAdmNo: e.target.value }))}
                  disabled={!!state?.learnerAdmNo} sx={{ flex: 1 }} />
                <TextField label="Stage" value={form.stage} size="small"
                  onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}
                  disabled={!!state?.stage} sx={{ minWidth: 100 }} />
              </Box>

              <Divider />

              {/* Charge Item */}
              <TextField select label="Charge Item *" value={form.chargeItemCode}
                onChange={e => handleChargeItem(e.target.value)} size="small" fullWidth>
                <MenuItem value=""><em>— Select charge item —</em></MenuItem>
                {chargeItems.map(c => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                    {c.glAccount && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>· {c.glAccount}</Typography>
                    )}
                  </MenuItem>
                ))}
              </TextField>

              {selectedItem && (
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip label={selectedItem.code} size="small" variant="outlined" color="primary" />
                  {selectedItem.applicableTo !== 'Both' && (
                    <Chip label={selectedItem.applicableTo} size="small" variant="outlined" />
                  )}
                  {selectedItem.isVatable && (
                    <Chip label={`VAT: ${selectedItem.vatPostingGroup}`} size="small" color="warning" variant="outlined" />
                  )}
                </Box>
              )}

              {/* Description */}
              <TextField label="Description" value={form.description} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Auto-filled from charge item — edit if needed" />

              {/* Reason */}
              <TextField label="Reason / Reference" value={form.reason} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. 2025 Term 1" />

              {/* Income GL Account — auto-picked from the charge item (read-only).
                  Receivables come from the learner's posting group on the backend. */}
              <TextField label="Income GL Account" size="small" fullWidth
                value={form.glAccount ? glLabel(form.glAccount) : ''}
                InputProps={{ readOnly: true }}
                placeholder="Set automatically from the selected charge item"
                helperText="Auto-set from the charge item · Receivables come from the learner's posting group" />

              {/* Amount */}
              <TextField label={`Amount (${sym()}) *`} type="number" value={form.amount} size="small"
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                fullWidth inputProps={{ min: 0, step: 0.01 }} />

              <Button variant="contained" size="large" startIcon={<Receipt />}
                onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : 'Create Invoice'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default BillingForm;
